import BodyText from '@lyra/core/components/BodyText'
import Countdown from '@lyra/core/components/Countdown'
import LabelText from '@lyra/core/components/LabelText'
import Table, {
  createColumnHelper,
  TableColumnDef,
  TableData,
  TableMarkerRow,
} from '@lyra/core/components/Table'
import { SECONDS_IN_DAY } from '@lyra/core/constants/time'
import formatRelativeDateTime from '@lyra/core/utils/formatRelativeDateTime'
import formatUSD from '@lyra/core/utils/formatUSD'
import { Board } from '@lyra/web/constants/instruments'
import { MarketId } from '@lyra/web/constants/markets'
import useMarkets from '@lyra/web/hooks/useMarkets'
import { getUtcNowSecs, getUtcSecs } from '@lyra/web/utils/time'
import { useMemo } from 'react'
import { StackProps } from 'tamagui'

import { TradeOptionTableTickerData } from '.'
import TradeOptionTableRowIndicator from './TradeOptionTableRowIndicator'

export const CALLS_GROUP_ID = 'calls'
export const PUTS_GROUP_ID = 'puts'

const STRIKE_COLUMN_WIDTH = 85

type Props = {
  selectedMarket: MarketId
  selectedBoard: Board
  callColumns: TableColumnDef<TradeOptionTableTickerData>[]
  putColumns: TableColumnDef<TradeOptionTableTickerData>[]
  rows: TableData<TradeOptionTableTickerData>[]
} & StackProps

const TradeOptionTableCallAndPut = ({
  selectedMarket,
  selectedBoard,
  callColumns,
  putColumns,
  rows,
  ...tableProps
}: Props) => {
  const columnHelper = createColumnHelper<TradeOptionTableTickerData>()
  const columns = useMemo(
    () => [
      columnHelper.group({
        id: CALLS_GROUP_ID,
        meta: {
          justifyContent: 'center',
        },
        header: 'Calls',
        columns: callColumns,
      }),
      columnHelper.group({
        id: 'expiry',
        meta: {
          minWidth: STRIKE_COLUMN_WIDTH,
          maxWidth: STRIKE_COLUMN_WIDTH,
          justifyContent: 'center',
          showLeftBorder: true,
          showRightBorder: true,
        },
        header: () => {
          const daysToExpiry = Math.ceil(
            (getUtcSecs(selectedBoard.expiry) - getUtcNowSecs()) / SECONDS_IN_DAY
          )

          if (daysToExpiry <= 2) {
            return (
              <LabelText>
                <Countdown endTimestamp={selectedBoard.expiry.getTime()} />
              </LabelText>
            )
          }

          const localHours = selectedBoard.expiry.getHours()
          const localHoursStr =
            localHours >= 12
              ? (localHours % 12) + 'PM'
              : localHours === 0
              ? '12AM'
              : localHours + 'AM'

          const expiryHeaderStr = `${formatRelativeDateTime(selectedBoard.expiry, {
            truncate: true,
          })} / ${localHoursStr}`

          return <LabelText>{expiryHeaderStr}</LabelText>
        },
        columns: [
          columnHelper.accessor('strikePrice', {
            id: 'strikePrice',
            meta: {
              minWidth: STRIKE_COLUMN_WIDTH,
              maxWidth: STRIKE_COLUMN_WIDTH,
              justifyContent: 'center',
              showLeftBorder: true,
              showRightBorder: true,
              isDisabled: true, // disables clickability of strikes column
            },
            header: 'Strike',
            cell: (props) => {
              const strikePrice = props.cell.row.original.strikePrice ?? 0
              const dps = strikePrice >= 100 ? 0 : 2
              return <BodyText>{formatUSD(strikePrice, { showCommas: false, dps })}</BodyText>
            },
          }),
        ],
      }),
      columnHelper.group({
        id: PUTS_GROUP_ID,
        meta: {
          justifyContent: 'center',
        },
        header: 'Puts',
        columns: putColumns,
      }),
    ],
    [callColumns, columnHelper, selectedBoard.expiry, putColumns]
  )

  const spotPrice = useMarkets()[selectedMarket].spotPrice

  const spotPriceMarker: TableMarkerRow = useMemo(() => {
    const spotPriceRowIdx = rows.reduce(
      (markerIdx, row) =>
        row.strikePrice && spotPrice < row.strikePrice ? markerIdx : markerIdx + 1,
      0
    )
    return {
      id: 'spot-marker',
      insertIndex: spotPriceRowIdx,
      autoCenter: true,
      content: <TradeOptionTableRowIndicator marketId={selectedMarket} />,
    }
  }, [selectedMarket, rows, spotPrice])

  return (
    <Table
      {...tableProps}
      horizontalCenter
      data={rows}
      columns={columns}
      markerRow={spotPriceMarker}
    />
  )
}

export default TradeOptionTableCallAndPut
